import { useState } from "react";
import { useKnowledgeItems, useDeleteKnowledgeItem, type KnowledgeItemType } from "@/hooks/knowledge/useKnowledgeItems";
import { useToast } from "@/hooks/use-toast";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    FileText,
    File,
    BookOpen,
    Link as LinkIcon,
    ChevronRight
} from "lucide-react";
import AddKnowledgeDropdown from "@/components/knowledge/AddKnowledgeDropdown";
import KnowledgeSection from "@/components/knowledge/KnowledgeSection";

type FilterType = "all" | "links" | "files" | "snippets";

interface AgentKnowledgeTabProps {
    agentId: string;
    agentType?: "regular" | "voice";
}

const AgentKnowledgeTab = ({ agentId, agentType = "regular" }: AgentKnowledgeTabProps) => {
    // Fetch agent-specific knowledge items
    const { items: agentItems, isLoading: agentLoading, refetch: refetchAgent } = useKnowledgeItems(undefined, agentId, agentType);
    
    // Fetch organization-wide knowledge items
    const { items: orgItems, isLoading: orgLoading, refetch: refetchOrg } = useKnowledgeItems(undefined, null);
    
    // Filter states for each section
    const [agentFilter, setAgentFilter] = useState<FilterType>("all");
    const [orgFilter, setOrgFilter] = useState<FilterType>("all");
    
    // Track accordion open state
    const [openSections, setOpenSections] = useState<string[]>(["agent-specific", "organization-wide"]);
    
    const { deleteItem } = useDeleteKnowledgeItem();
    const { toast } = useToast();

    const handleAddSuccess = () => {
        refetchAgent();
        toast({
            title: "Knowledge added",
            description: "Your knowledge item has been added successfully",
        });
    };

    const handleDelete = async (id: string, type: string, filePath?: string) => {
        const success = await deleteItem(id, type as KnowledgeItemType, filePath);
        if (success) {
            refetchAgent();
            refetchOrg();
            toast({
                title: "Success",
                description: "Item deleted successfully",
            });
        }
        return success;
    };



    const toggleSection = (sectionId: string) => {
        setOpenSections(prev => 
            prev.includes(sectionId) 
                ? prev.filter(id => id !== sectionId)
                : [...prev, sectionId]
        );
    };

    const isOpen = (sectionId: string) => openSections.includes(sectionId);

    return (
        <div className="space-y-6">
            {/* Header Row */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold">Knowledge items</h2>
                    <p className="text-sm text-muted-foreground">
                        Train your agents on files, websites or text snippets
                    </p>
                </div>
                <AddKnowledgeDropdown onSuccess={handleAddSuccess} agentId={agentId} />
            </div>

            {/* Accordion Sections */}
            <Accordion type="multiple" value={openSections} onValueChange={setOpenSections} className="w-full">
                {/* Agent-specific knowledge section */}
                <AccordionItem value="agent-specific" className="border-0">
                    <div className="flex items-center justify-between py-4 cursor-pointer" onClick={() => toggleSection("agent-specific")}>
                        <div className="flex items-center gap-3">
                            <ChevronRight 
                                className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
                                    isOpen("agent-specific") ? "rotate-90" : ""
                                }`} 
                            />
                            <div className="text-left">
                                <h3 className="text-base font-medium">For this agent only</h3>
                                <p className="text-sm text-muted-foreground font-normal">Knowledge items specific to this agent</p>
                            </div>
                        </div>
                        <div onClick={(e) => e.stopPropagation()}>
                            <Tabs value={agentFilter} onValueChange={(value) => setAgentFilter(value as FilterType)}>
                                <TabsList className="h-8">
                                    <TabsTrigger value="all" className="text-xs px-2 py-1">
                                        <BookOpen className="mr-1 h-3 w-3" />
                                        All ({agentItems.length})
                                    </TabsTrigger>
                                    <TabsTrigger value="links" className="text-xs px-2 py-1">
                                        <LinkIcon className="mr-1 h-3 w-3" />
                                        Links ({agentItems.filter(item => item.type === "link").length})
                                    </TabsTrigger>
                                    <TabsTrigger value="files" className="text-xs px-2 py-1">
                                        <File className="mr-1 h-3 w-3" />
                                        Files ({agentItems.filter(item => item.type === "file").length})
                                    </TabsTrigger>
                                    <TabsTrigger value="snippets" className="text-xs px-2 py-1">
                                        <FileText className="mr-1 h-3 w-3" />
                                        Snippets ({agentItems.filter(item => item.type === "snippet").length})
                                    </TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </div>
                    </div>
                    <AccordionContent className="pt-0">
                        <KnowledgeSection
                            title=""
                            subtitle=""
                            items={agentItems}
                            isLoading={agentLoading}
                            activeFilter={agentFilter}
                            agentId={agentId}
                            showAddButton={false}
                            showRefreshButton={false}
                            onAddSuccess={handleAddSuccess}
                            onDelete={handleDelete}
                            onEdit={refetchAgent}
                            onRefresh={refetchAgent}
                        />
                    </AccordionContent>
                </AccordionItem>

                {/* Organization-wide knowledge section */}
                <AccordionItem value="organization-wide" className="border-0">
                    <div className="flex items-center justify-between py-4 cursor-pointer" onClick={() => toggleSection("organization-wide")}>
                        <div className="flex items-center gap-3">
                            <ChevronRight 
                                className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
                                    isOpen("organization-wide") ? "rotate-90" : ""
                                }`} 
                            />
                            <div className="text-left">
                                <h3 className="text-base font-medium">Organization knowledge</h3>
                                <p className="text-sm text-muted-foreground font-normal">Accessible to all agents in your organization</p>
                            </div>
                        </div>
                        <div onClick={(e) => e.stopPropagation()}>
                            <Tabs value={orgFilter} onValueChange={(value) => setOrgFilter(value as FilterType)}>
                                <TabsList className="h-8">
                                    <TabsTrigger value="all" className="text-xs px-2 py-1">
                                        <BookOpen className="mr-1 h-3 w-3" />
                                        All ({orgItems.length})
                                    </TabsTrigger>
                                    <TabsTrigger value="links" className="text-xs px-2 py-1">
                                        <LinkIcon className="mr-1 h-3 w-3" />
                                        Links ({orgItems.filter(item => item.type === "link").length})
                                    </TabsTrigger>
                                    <TabsTrigger value="files" className="text-xs px-2 py-1">
                                        <File className="mr-1 h-3 w-3" />
                                        Files ({orgItems.filter(item => item.type === "file").length})
                                    </TabsTrigger>
                                    <TabsTrigger value="snippets" className="text-xs px-2 py-1">
                                        <FileText className="mr-1 h-3 w-3" />
                                        Snippets ({orgItems.filter(item => item.type === "snippet").length})
                                    </TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </div>
                    </div>
                    <AccordionContent className="pt-0">
                        <KnowledgeSection
                            title=""
                            subtitle=""
                            items={orgItems}
                            isLoading={orgLoading}
                            activeFilter={orgFilter}
                            agentId={null}
                            showAddButton={false}
                            showRefreshButton={false}
                            onDelete={undefined} // Read-only for org items
                            onEdit={undefined}   // Read-only for org items
                            onRefresh={undefined}
                        />
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </div>
    );
};

export default AgentKnowledgeTab; 